// ---- Named body poses (seated / sitting / prone / all-fours / kneeling / …) ----
// The blended FULL-BODY pose layer: a host sets `root.userData.bodyPose` to a
// pose id (content/anim/clips.json `poses`, via bodyPose() in clips.ts) or null,
// and applyBodyPose cross-fades the rig into/out of/between poses each frame —
// the N-pose generalization of the carry↔ready stance ease in blend.ts. Runs as
// a FINAL override pass AFTER gait + applyWeaponPose (same layering contract as
// the block/fishing/reading poses in player.ts), so the joints a pose defines
// win over everything below.
//
// ROOT RULE (the topple precedent, src/anim/reactions.ts): this module NEVER
// touches group.position/rotation. The rig root is the FEET with the pelvis
// rigidly ~1.06·scale·legLen above it, so sit/lie poses carry a root offset
// (y drop + pitch/roll) — applyBodyPose reports the blended offset through the
// `out` param and returns true while any of it is live; the CALLER integrates
// it (additive over its feetY resolve, rotation.x/z alongside its own root
// writes, facing rotation.y untouched). While a host's defeat/topple owns the
// root, skip this pass entirely.
//
// Client-visual only: the pure sim (src/sim/combat.ts) is pose-free and the
// parity/snapshot tests can't see any of this. Wire state is just the pose id
// string (NetPlayer/NetNpc.pose) — the blend is derived locally, like
// combatReady/attackT already are.

import { bodyPose, type Vec3 } from "./clips.ts";
import type { PoseChannel } from "./clips.ts";
import { applyStanceBlended, captureStanceFrom, lerp, stanceChannels } from "./blend.ts";
import type { RigHandle } from "../model/rig.ts";

/** Fallback fade windows (seconds) when a pose doesn't author `fade`. Mutable so
 *  the studio can scrub them live — client-visual, NOT a sim tunable. */
export const POSE_BLEND = { defaultEnter: 0.4, defaultExit: 0.25 };

/** The blended root offset a pose currently applies — dy is ADDITIVE over the
 *  caller's feetY; pitch/roll are rotation.x/z radians. */
export type PoseRootOut = { dy: number; pitch: number; roll: number };

type PoseState = {
  cur: string | null; // pose being displayed / blended toward (null = standing)
  w: number; // transition progress 0→1
  dur: number; // seconds for the current transition
  from: Record<string, Vec3>; // captured joint ease-from
  chans: PoseChannel[]; // channels the current transition touches
  // Pristine per-channel eulers, captured the FIRST time a channel enters a
  // transition — i.e. before this layer ever wrote it. The exit fade settles
  // y/z back to these: the gait only rewrites rotation.x on most joints, so
  // the live y/z euler mid-exit is our own previous write (a residue that
  // would otherwise freeze in — legs stuck together after a sit).
  rest: Record<string, Vec3>;
  rootFrom: [number, number, number]; // [dy, pitch, roll] at transition start
  rootTo: [number, number, number];
  out: [number, number, number]; // displayed root offset (blend result)
};

/** Hosts that only need to know "is a pose live" without running the blend
 *  (e.g. gating other overrides) can peek at the flag. */
export function poseActive(root: { userData: Record<string, unknown> }): boolean {
  return root.userData._poseState !== undefined || !!root.userData.bodyPose;
}

/**
 * The per-frame body-pose pass. Reads `root.userData.bodyPose` (pose id or
 * null/undefined = standing), eases joints via the blend.ts stance machinery,
 * and writes the blended root offset into `out`. Returns true while a pose (or
 * its exit fade) is influencing the rig — when false, `out` is zeroed and the
 * caller should apply no root offset.
 */
export function applyBodyPose(
  root: { userData: Record<string, unknown> },
  rig: RigHandle,
  dt: number,
  out: PoseRootOut,
): boolean {
  const ud = root.userData;
  let target = (ud.bodyPose as string | null | undefined) ?? null;
  let spec = target ? bodyPose(target) : undefined;
  if (target && !spec) { target = null; spec = undefined; } // unknown id → stand (wire tolerance)

  let st = ud._poseState as PoseState | undefined;
  if (!st && !target) { out.dy = 0; out.pitch = 0; out.roll = 0; return false; } // fast path
  if (!st) {
    st = {
      cur: null, w: 1, dur: 0, from: {}, chans: [], rest: {},
      rootFrom: [0, 0, 0], rootTo: [0, 0, 0], out: [0, 0, 0],
    };
    ud._poseState = st;
  }

  if (target !== st.cur) {
    const leaving = st.cur ? bodyPose(st.cur) : undefined;
    // Entering/switching uses the ENTERING pose's fade; standing up uses the
    // LEAVING pose's exit fade.
    st.dur = spec
      ? spec.fade?.enter ?? POSE_BLEND.defaultEnter
      : leaving?.fade?.exit ?? POSE_BLEND.defaultExit;
    st.chans = stanceChannels(leaving?.joints, spec?.joints);
    // Rest capture: only channels this state has never touched — the current
    // euler is pristine exactly then (later transitions read our own writes).
    for (const ch of st.chans) {
      if (st.rest[ch]) continue;
      const g = rig[ch];
      st.rest[ch] = g ? [g.rotation.x, g.rotation.y, g.rotation.z] : [0, 0, 0];
    }
    st.from = {};
    // Ease-from: the leaving pose's authored values where it speaks, else the
    // live rig euler (gait) — the exact captureStanceFrom convention carry↔ready
    // uses (a mid-blend flip approximates from the full leaving pose).
    captureStanceFrom(rig, leaving?.joints, st.chans, st.from);
    st.rootFrom = [st.out[0], st.out[1], st.out[2]];
    st.rootTo = spec?.root ? [spec.root.y ?? 0, spec.root.pitch ?? 0, spec.root.roll ?? 0] : [0, 0, 0];
    st.w = 0;
    st.cur = target;
  }

  if (st.w < 1) st.w = st.dur > 0 ? Math.min(1, st.w + dt / st.dur) : 1;
  const e = st.w * st.w * (3 - 2 * st.w); // smoothstep — settles without a hard stop

  const toPose = st.cur ? bodyPose(st.cur)?.joints ?? {} : {};
  applyStanceBlended(rig, st.from, toPose, e, st.rest);
  st.out[0] = lerp(st.rootFrom[0], st.rootTo[0], e);
  st.out[1] = lerp(st.rootFrom[1], st.rootTo[1], e);
  st.out[2] = lerp(st.rootFrom[2], st.rootTo[2], e);
  out.dy = st.out[0];
  out.pitch = st.out[1];
  out.roll = st.out[2];

  if (st.cur === null && st.w >= 1) {
    // Exit fade finished — drop the state so the fast path resumes.
    delete ud._poseState;
    out.dy = 0; out.pitch = 0; out.roll = 0;
    return false;
  }
  return true;
}

/** Snap a rig straight into a pose at full weight (no transition) — the studio
 *  scrub / inspect-model / initial-spawn path (an NPC authored seated shouldn't
 *  sit down on world entry). */
export function snapBodyPose(
  root: { userData: Record<string, unknown> },
  rig: RigHandle,
  id: string | null,
  out: PoseRootOut,
): boolean {
  root.userData.bodyPose = id;
  delete root.userData._poseState;
  return applyBodyPose(root, rig, Infinity, out);
}
